import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { View, ViewStyle, StyleProp } from "react-native";
import { StyleFunction, useTheme, useThemedStyle, ITheme } from "../../theme";
import { contrastToBlack } from "../../utils";
import { Icon } from "../icon";
import { Text } from "../text";

export type AlertType = "success" | "warning" | "error" | "info";

export const Alert = React.memo(
    function AlertFunc({
        children,
        type,
        style,
        delay,
        swapColors = false,
        testID,
    }: {
        type: AlertType;
        children: ReactNode;
        style?: StyleProp<ViewStyle>;
        delay?: number;
        swapColors?: boolean;
        testID?: string;
    }) {
        const styles = useThemedStyle(styleFunc);
        const theme = useTheme();
        const { bgColor, textColor } = useMemo(() => {
            const baseColor = getAlertColor(type, theme);
            const calculatedTextColor = contrastToBlack(baseColor)
                ? theme.colors.white
                : theme.colors.black;

            if (swapColors) {
                return { bgColor: calculatedTextColor, textColor: baseColor };
            }
            return { bgColor: baseColor, textColor: calculatedTextColor };
        }, [type, swapColors, theme]);

        const containerStyle = useMemo(
            () => [styles.alert, { backgroundColor: bgColor }, style],
            [styles.alert, bgColor, style]
        );

        const textStyle = useMemo(
            () => [styles.message, { color: textColor }],
            [styles.message, textColor]
        );

        const [hidden, setHidden] = useState<boolean>(delay !== undefined);
        useEffect(() => {
            if (!delay) {
                return;
            }

            const timeout = setTimeout(() => {
                setHidden(false);
            }, delay);
            return () => {
                clearTimeout(timeout);
            };
        }, [delay]);

        if (hidden) {
            return null;
        }

        return (
            <View style={containerStyle}>
                <Icon name={type} color={textColor} />
                <View style={styles.textContainer}>
                    <Text style={textStyle} testID={testID}>
                        {children}
                    </Text>
                </View>
            </View>
        );
    },
    (prev, next) =>
        prev.type === next.type &&
        prev.delay === next.delay &&
        prev.swapColors === next.swapColors &&
        prev.testID === next.testID
);

function getAlertColor(type: AlertType, theme: ITheme) {
    switch (type) {
        case "error":
            return theme.colors.error;
        case "warning":
            return theme.colors.warning;
        case "info":
            return theme.colors.info;
        case "success":
            return theme.colors.success;
        default:
            return theme.colors.toastDefault;
    }
}

const styleFunc: StyleFunction = theme => ({
    textContainer: {
        ...theme.styles.flex1,
    },
    alert: {
        ...theme.styles.row,
        columnGap: theme.spacingScale,

        padding: theme.spacingScale,
        borderRadius: theme.borderRadiusTiny,

        // Native elevation
        elevation: 2,

        // Web elevation
        shadowColor: theme.colors.grey800,
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.3,
        shadowRadius: 1.5,
    },
    message: {
        ...theme.styles.text,
    },
});
